import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
  TablePagination,
  styled,
  tableCellClasses,
  Box,
  TextField,
  Backdrop,
  CircularProgress,
  Skeleton,
  Grid,
  Avatar,
  Divider,
  Card,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  Edit,
  Delete,
  Visibility,
  FilterList,
  Add,
  Search,
  RestartAlt,
} from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import { decryptId, encryptId } from "../../Components/CryptoComponent";
import SelectInput from "../../Components/InputComponents/SelectInput";
import {
  errorNotify,
  successNotify,
} from "../../Components/ToastComponent/ToastNotification";
import CommonButton from "../../Components/InputComponents/CommonButton";
import { useStaffLayout } from "../../Components/Context/CandidateLayoutContext";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const ClientJobApplicantList = ({ isAdmin = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { id, title } = useParams();
  const [decryptedId, setdecryptedId] = useState(
    decryptId(decodeURIComponent(id)),
  );

  const decryptedTitle = decryptId(decodeURIComponent(title));

  const [applicantData, setApplicantData] = useState([]);

  const [jobData, setJobData] = useState({});

  const [totalJoined, setTotalJoined] = useState();
  const [totalInterviewAttended, setTotalInterviewAttended] = useState();
  const [totalInterviewSelected, setTotalInterviewSelected] = useState();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pageCount, setPageCount] = useState("");

  const [jobStatus, setJobStatus] = useState(1); // 1 = Active, 2 = Deployed, 3 = Cancelled

  const [loaderOpen, setLoaderOpen] = useState(true);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleApplyFilters = () => {
    getData();
  };

  const [filterData, setFilterData] = useState({
    CI_Name: "",
    CI_PassportNo: "",
    CI_PrimaryMobile: "",
    CI_PrimaryEmail: "",
  });

  const paginationState = {
    pagination: { page, rowsPerPage },
    filterData: filterData,
  };

  const [statusList, setStatusList] = useState([]);

  const getStatus = async () => {
    try {
      const res = await axiosAPI.get(`/JobApplicationStatus/list`);
      setStatusList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const [candidateID, setCandidateID] = useState("");
  const [JobApplicationId, setJobApplicationId] = useState("");

  const handleStatusChange = (e) => {
    axiosAPI
      ?.post(`/Candidate/updateInterviewStatus/${JobApplicationId}`, {
        JA_CandidateId: candidateID,
        JA_JobId: jobData?.JD_Id,
        JA_StatusId: e?.target.value,
      })
      .then((res) => {
        if (res?.data?.success) {
          successNotify(res?.data?.data?.message);
          getData();
        } else {
          errorNotify(res?.data?.data?.message);
        }
      })
      .catch((err) => {
        errorNotify(`Something went wrong`);
      });
  };

  const handleResetFilters = () => {
    const resetFilters = {
      CI_Name: "",
      CI_PassportNo: "",
      CI_PrimaryMobile: "",
      CI_PrimaryEmail: "",
    };

    const isAlreadyReset = Object.entries(resetFilters).every(
      ([key, value]) => filterData[key] === value,
    );

    if (!isAlreadyReset) {
      setFilterData(resetFilters);
      getData(resetFilters);
    }

    const encrypted = encodeURIComponent(encryptId(decryptedId));
    const encryptedTitle = encodeURIComponent(encryptId(decryptedTitle));

    navigate(`/Jobs/Applicants/${encrypted}/${encryptedTitle}`, {
      replace: true,
    });
  };

  const getJobData = async () => {
    try {
      const res = await axiosAPI.get(`/Job/listbyIdForView/${decryptedId}`);
      setJobData(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getData = (filter = filterData) => {
    const trimmedFilters = {
      CI_Name: filter?.CI_Name,
      CI_PassportNo: filter?.CI_PassportNo,
      CI_PrimaryMobile: filter?.CI_PrimaryMobile,
      CI_PrimaryEmail: filter?.CI_PrimaryEmail,
      page: filter?.page ?? page + 1,
      limit: filter?.limit ?? rowsPerPage,
      jobstat: filter?.jobstat ?? jobStatus,
    };
    setLoaderOpen(true);

    axiosAPI
      ?.post(`/clientportal/CandidatebyJob/${decryptedId}`, trimmedFilters)

      .then((res) => {
        setPageCount(res?.data?.data?.totalRecords);

        setApplicantData(res?.data?.data?.candidates);

        setTotalJoined(res?.data?.data?.totalJoined);
        setTotalInterviewAttended(res?.data?.data?.totalInterviewAttended);
        setTotalInterviewSelected(res?.data?.data?.totalInterviewSelected);

        setTimeout(() => {
          setLoaderOpen(false);
        }, 500);
      })
      .catch((err) => {
        errorNotify(`Something went wrong`);
        setLoaderOpen(false);
      });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (decryptedId) {
      getJobData();
      getData();
    }
  }, [page, rowsPerPage, decryptedId]);

  useEffect(() => {
    getStatus();
  }, []);

  const { privilegeData } = useStaffLayout();

  function hasCandidateViewprivilege(privilegeData) {
    if (isAdmin) return true;
    return privilegeData.some((subModule) => {
      const isCandidate = subModule.subModuleName
        .toLowerCase()
        .includes("candidate");
      if (!isCandidate) return false;

      return subModule.privileges.some(
        (priv) =>
          priv.privilegeName.toLowerCase() === "view" && priv.assigned === true,
      );
    });
  }

  const nativatetoViewCandidate = (c) => {
    if (!hasCandidateViewprivilege(privilegeData)) {
      return;
    }

    const encrypted = encodeURIComponent(encryptId(c?.CI_Id));
    navigate(`/Candidates/View/${encrypted}`, {
      state: { ...paginationState, fromRoute: "JobApplicantList" },
    });
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingEvent, setPendingEvent] = useState(null);

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        sx={{ flexDirection: { xs: "column", sm: "row" } }}
      >
        <Box display="flex" alignItems="center" flexWrap="wrap">
          <CommonButton
            variant="outlined"
            background="#006CB8"
            onClick={() =>
              navigate(`/Jobs`, {
                state: {
                  pagination: paginationState,
                  filterData: filterData,
                },
              })
            }
            margin="0px 20px 10px 0px"
          >
            <ArrowBackIcon sx={{ color: "white" }} />
          </CommonButton>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              fontFamily: "Geist, sans-serif",
            }}
          >
            Job Applicants {decryptedTitle ? `of ${decryptedTitle}` : null}
          </Typography>
        </Box>
      </Grid>

      <Grid container spacing={2} mb={2}>
        {/* Left: Job Info (8/12) */}
        <Grid item xs={12} md={9} lg={9}>
          <Card
            variant="outlined"
            sx={{ p: 2, minHeight: { md: "none", lg: "280px" } }}
          >
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4} md={3} lg={4} xl={3}>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "18px",
                    color: "#333",
                    fontWeight: "600",
                  }}
                >
                  Job ID
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: "400",
                  }}
                >
                  {jobData?.JD_JobId}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg={4} xl={3}>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "18px",
                    color: "#333",
                    fontWeight: "600",
                  }}
                >
                  Title
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: "400",
                  }}
                >
                  {jobData?.JD_Title}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg={4} xl={3}>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "18px",
                    color: "#333",
                    fontWeight: "600",
                  }}
                >
                  Location
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: "400",
                  }}
                >
                  {jobData?.JD_Locations}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg={4} xl={3}>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "18px",
                    color: "#333",
                    fontWeight: "600",
                  }}
                >
                  Client
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: "400",
                  }}
                >
                  {jobData?.JD_Client?.Name}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg={4} xl={3}>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "18px",
                    color: "#333",
                    fontWeight: "600",
                  }}
                >
                  Country
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: "400",
                  }}
                >
                  {jobData?.JD_Country?.Name}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg={4} xl={3}>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "18px",
                    color: "#333",
                    fontWeight: "600",
                  }}
                >
                  Department
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: "400",
                  }}
                >
                  {jobData?.JD_Department ? jobData?.JD_Department : "-"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg={4} xl={3}>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "18px",
                    color: "#333",
                    fontWeight: "600",
                  }}
                >
                  Profession
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: "400",
                  }}
                >
                  {jobData?.JD_Profession?.Name
                    ? jobData?.JD_Profession?.Name
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg={4} xl={3}>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "18px",
                    color: "#333",
                    fontWeight: "600",
                  }}
                >
                  Salary
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: "0.5px",
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: "400",
                  }}
                >
                  {jobData?.JD_SalaryRange &&
                  jobData?.JD_Currency?.Code &&
                  jobData?.JD_SalaryPay
                    ? ` ${jobData?.JD_SalaryRange} ${jobData?.JD_Currency?.Code} / ${jobData?.JD_SalaryPay}`
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg={4} xl={3}>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "18px",
                    color: "#333",
                    fontWeight: "600",
                  }}
                >
                  Age Limit
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: "400",
                  }}
                >
                  {jobData?.JD_MinAge && jobData?.JD_MaxAge
                    ? `${jobData?.JD_MinAge} - ${jobData?.JD_MaxAge}`
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg={4} xl={3}>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "18px",
                    color: "#333",
                    fontWeight: "600",
                  }}
                >
                  Open Date
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: "400",
                  }}
                >
                  {jobData?.JD_OpenDate}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg={4} xl={3}>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "18px",
                    color: "#333",
                    fontWeight: "600",
                  }}
                >
                  Close Date
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: "400",
                  }}
                >
                  {jobData?.JD_CloseDate}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg={4} xl={3}>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "18px",
                    color: "#333",
                    fontWeight: "600",
                  }}
                >
                  No. of Openings
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: "400",
                  }}
                >
                  {jobData?.JD_NoofOpening ? jobData?.JD_NoofOpening : "-"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "18px",
                    color: "#333",
                    fontWeight: "600",
                  }}
                >
                  Qualification
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: "400",
                  }}
                >
                  {Array.isArray(jobData.JD_Qualification)
                    ? jobData.JD_Qualification.map((a) => a?.Name).join(", ")
                    : jobData.JD_Qualification || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "18px",
                    color: "#333",
                    fontWeight: "600",
                  }}
                >
                  Gender
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: `0.5px`,
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: "400",
                  }}
                >
                  {Array.isArray(jobData.JD_Gender)
                    ? jobData.JD_Gender.map((a) => a?.Name).join(", ")
                    : jobData.JD_Gender || "-"}
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Right: Status Boxes (4/12) */}
        <Grid item xs={12} md={3} lg={3}>
          <Card
            variant="outlined"
            sx={{
              p: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Grid container spacing={2} sx={{ flexGrow: 1 }}>
              {/* Card Wrapper for Each Status */}
              {[
                {
                  label: "Total Joined",
                  value: totalJoined,
                  bg: "#e3f2fd",
                },
                {
                  label: "Interview Attended",
                  value: totalInterviewAttended,
                  bg: "#fff3e0",
                },
                {
                  label: "Interview Selected",
                  value: totalInterviewSelected,
                  bg: "#e8f5e9",
                },
              ].map((item, index) => (
                <Grid item xs={12} key={index}>
                  <Card
                    sx={{
                      backgroundColor: item.bg,
                      p: 2,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography
                        sx={{
                          fontFamily: "Geist, sans-serif",
                          letterSpacing: "0.5px",
                          fontSize: "18px",
                          color: "#333",
                          fontWeight: "600",
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Typography variant="h5">{item.value ?? 0}</Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{ width: "100%", mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4} md={4} lg={3} xl={3}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Name
            </Typography>
            <TextField
              placeholder="Name"
              variant="outlined"
              size="small"
              value={filterData.CI_Name}
              onChange={(e) =>
                setFilterData((prev) => ({ ...prev, CI_Name: e.target.value }))
              }
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={4} lg={3} xl={3}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Passport Number
            </Typography>
            <TextField
              placeholder="Passport Number"
              variant="outlined"
              size="small"
              value={filterData.CI_PassportNo}
              onChange={(e) =>
                setFilterData((prev) => ({
                  ...prev,
                  CI_PassportNo: e.target.value,
                }))
              }
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={4} lg={3} xl={3}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Mobile Number
            </Typography>
            <TextField
              placeholder="Mobile Number"
              variant="outlined"
              size="small"
              value={filterData.CI_PrimaryMobile}
              onChange={(e) =>
                setFilterData((prev) => ({
                  ...prev,
                  CI_PrimaryMobile: e.target.value,
                }))
              }
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={4} lg={3} xl={3}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Email
            </Typography>
            <TextField
              placeholder="Email"
              variant="outlined"
              size="small"
              value={filterData.CI_PrimaryEmail}
              onChange={(e) =>
                setFilterData((prev) => ({
                  ...prev,
                  CI_PrimaryEmail: e.target.value,
                }))
              }
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Buttons */}
      <Box
        sx={{
          width: "100%", // Full width
          display: "flex",
          justifyContent: "flex-end", // Align buttons to the right
          alignItems: "flex-end",
          gap: 1,
          my: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<Search />}
          onClick={handleApplyFilters}
          sx={{
            textTransform: "capitalize",
            color: "white",
            background: "#006CB8",
          }}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          startIcon={<RestartAlt />}
          onClick={handleResetFilters}
          sx={{ textTransform: "capitalize" }}
        >
          Reset
        </Button>
      </Box>

      {/* Outer scroll container */}
      <Box sx={{ width: "100%", overflowX: "auto", minHeight: "450px" }}>
        <ToggleButtonGroup
          value={jobStatus}
          exclusive
          onChange={(e, newValue) => {
            if (newValue !== null) setJobStatus(newValue);

            getData({
              ...filterData,
              jobstat: newValue, // optional if already read inside getData
              page: 1,
              limit: 25,
            });
          }}
          sx={{
            backgroundColor: "#f5f7fa",
            borderRadius: "8px",
            marginY: "10px",
          }}
        >
          <ToggleButton
            value={1}
            sx={{
              "&.Mui-selected": {
                backgroundColor: "#006CB8",
                color: "white",
                transition: "all 0.3s ease-in-out",
                "&:hover": { backgroundColor: "#006CB8" },
              },
            }}
          >
            Active
          </ToggleButton>

          <ToggleButton
            value={2}
            sx={{
              "&.Mui-selected": {
                backgroundColor: "#006CB8",
                color: "white",
                transition: "all 0.3s ease-in-out",
                "&:hover": { backgroundColor: "#006CB8" },
              },
            }}
          >
            Deployed
          </ToggleButton>

          <ToggleButton
            value={3}
            sx={{
              "&.Mui-selected": {
                backgroundColor: "#006CB8",
                color: "white",
                transition: "all 0.3s ease-in-out",
                "&:hover": { backgroundColor: "#006CB8" },
              },
            }}
          >
            Cancelled
          </ToggleButton>
        </ToggleButtonGroup>
        <>
          <TableContainer
            sx={{
              minWidth: 1600,
              borderRadius: "10px",
              border: "1px solid #e5e7eb", // outer light gray border
              overflow: "auto",
            }}
          >
            <Table stickyHeader>
              {/* ---------------- HEADER ---------------- */}
              <TableHead>
                <TableRow>
                  {[
                    "Name",
                    ...(jobStatus != 3 ? ["Profession"] : []),
                    "Passport Number",
                    // ...(jobStatus != 3 ? ["Phone"] : []),
                    // ...(jobStatus != 3 ? ["Email"] : []),
                    ...(jobStatus == 1 ? ["CV Sent"] : []),
                    ...(jobStatus == 1 ? ["Job Offer Status"] : []),
                    ...(jobStatus == 3 ? ["Cancellation Remarks"] : []),
                    ...(jobStatus == 1 ? ["Interview Status"] : []),
                    ...(hasCandidateViewprivilege(privilegeData)
                      ? ["Actions"]
                      : []),
                  ].map((head) => (
                    <TableCell
                      key={head}
                      sx={{
                        backgroundColor: "#006CB8",
                        color: "white",
                        fontWeight: 600,
                        borderBottom: "1px solid #d1d5db",
                        whiteSpace: "nowrap",
                        textAlign: "left",
                      }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              {/* ---------------- BODY ---------------- */}
              <TableBody>
                {loaderOpen ? (
                  Array.from({ length: 7 }).map((_, idx) => (
                    <TableRow key={idx}>
                      {Array.from({ length: jobStatus == 1 ? 7 : 4 }).map(
                        (_, index) => (
                          <StyledTableCell key={index}>
                            <Skeleton
                              variant="rectangular"
                              width="100%"
                              height={40}
                            />
                          </StyledTableCell>
                        ),
                      )}
                    </TableRow>
                  ))
                ) : applicantData && applicantData.length > 0 ? (
                  applicantData.map((parent, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        "& td": {
                          borderBottom: "1px solid #f0f0f0", // soft row lines
                        },
                      }}
                    >
                      {/* Name */}
                      <TableCell>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          onClick={() => nativatetoViewCandidate(parent)}
                          sx={{cursor:hasCandidateViewprivilege(privilegeData)?"pointer":''}}
                        >
                          <Avatar
                            src={
                              parent?.CI_Photo
                                ? `${process.env.REACT_APP_BASE_URL}/${parent.CI_Photo}`
                                : undefined
                            }
                            sx={{ width: 46, height: 46 }}
                          >
                            {parent?.CI_Name?.[0] || ""}
                          </Avatar>
                          <Typography variant="body2">
                            {parent?.CI_Name || "-"}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Profession */}
                      {jobStatus != 3 && (
                        <TableCell>
                          <Typography variant="body2">
                            {parent.profession_Name || "-"}
                          </Typography>
                        </TableCell>
                      )}

                      {/* Passport */}
                      <TableCell>
                        <Typography variant="body2">
                          {parent.CI_PassportNo || "-"}
                        </Typography>
                      </TableCell>

                      {/* CV Sent */}
                      {jobStatus == 1 && (
                        <TableCell>
                          <Typography variant="body2">
                            {parent.JA_Cvsent ? "Yes" : "No"}
                          </Typography>
                        </TableCell>
                      )}

                      {/* Job Offer */}
                      {jobStatus == 1 && (
                        <TableCell>
                          <Typography variant="body2">
                            {parent.JA_Shortlisted ? "Yes" : "No"}
                          </Typography>
                        </TableCell>
                      )}

                      {/* JA_CancellationRemarks */}
                      {jobStatus == 3 && (
                        <TableCell>
                          <Typography variant="body2">
                            {parent.JA_CancellationRemarks || "-"}
                          </Typography>
                        </TableCell>
                      )}

                      {/* Interview Status */}
                      {jobStatus == 1 ? (
                        <TableCell sx={{ minWidth: 220 }}>
                          <FormControl fullWidth size="small">
                            <SelectInput
                              value={parent?.JASL_Id}
                              name="jobapplication"
                              onChange={(e) => {
                                setCandidateID(parent?.CI_Id);
                                setJobApplicationId(parent?.JA_Id);
                                setPendingEvent(e);
                                setConfirmOpen(true);
                              }}
                              options={statusList?.map((item) => ({
                                value: item?.JASL_Id,
                                label: item?.JASL_Name,
                              }))}
                              placeholder="Select Status"
                            />
                          </FormControl>
                        </TableCell>
                      ) : null}

                      <TableCell>
                        {hasCandidateViewprivilege(privilegeData) && (
                          <IconButton
                            color="primary"
                            onClick={() => nativatetoViewCandidate(parent)}
                            disabled={!hasCandidateViewprivilege(privilegeData)}
                          >
                            <Visibility />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Box
                        height={150}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Typography
                          sx={{
                            fontFamily: "Geist, sans-serif",
                            letterSpacing: "0.5px",
                            fontSize: "24px",
                            color: "grey",
                            fontWeight: 600,
                          }}
                        >
                          No data to show
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={pageCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 75, 100]}
            sx={{
              // General font size for the pagination root
              fontSize: { xs: "12px", sm: "14px" },
              // Specific font size for the "Rows per page" label
              "& .MuiTablePagination-selectLabel": {
                fontSize: { xs: "12px", sm: "14px" },
                fontFamily: "Geist, sans-serif", // Optional
              },
            }}
          />
        </>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            color: "#0977BC",
            mt: 3,
            mb: 2,
            fontFamily: "Geist, sans-serif",
          }}
        >
          Confirm Status Change
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to change the status?
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ mb: 3, mr: 3 }}>
          <Box display="flex" gap={2}>
            <CommonButton
              variant="outlined"
              customColor="#B0BEC5"
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </CommonButton>
            <CommonButton
              variant="contained"
              customColor="#007BFF"
              onClick={() => {
                handleStatusChange(pendingEvent);
                setConfirmOpen(false);
              }}
            >
              Yes
            </CommonButton>
          </Box>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ClientJobApplicantList;
